/**
 * Users API hooks — admin user management.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import type { Paginated } from '@shared/types';

export interface UserListItem {
  id: string;
  email: string;
  name: string;
  role: 'Admin' | 'Manager' | 'Employee';
  employeeId: string | null;
}

export type UserUpdateBody = {
  name?: string;
  email?: string;
  employeeId?: string | null;
};

export const userKeys = {
  all: ['users'] as const,
  list: () => [...userKeys.all, 'list'] as const,
};

export function useUsers(opts?: { limit?: number }) {
  const limit = opts?.limit ?? 1000;
  return useQuery({
    queryKey: [...userKeys.list(), { limit }] as const,
    queryFn: () => api.get<Paginated<UserListItem>>(`/api/users?limit=${limit}`),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; name: string; password: string; role: string; employeeId?: string | null }) =>
      api.post<UserListItem>('/api/users', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.all });
      void qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export function useResetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
      api.post<{ ok: boolean }>(`/api/users/${id}/reset-password`, { newPassword }),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: 'Admin' | 'Manager' | 'Employee' }) =>
      api.put<{ ok: boolean }>(`/api/users/${id}/role`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

/** Update user profile / employee link (Admin). */
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UserUpdateBody }) =>
      api.put<UserListItem>(`/api/users/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: userKeys.all }),
  });
}

/** Delete a user (Admin only; cannot delete self). */
export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`/api/users/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
